export const NOTIFICATIONS_TAG = 'notifications-bell'
export const NOTIFICATION_UI_SCRIPT_ID = 'notificationUiUrl'
export const NOTIFICATION_UI_SCRIPT_PATH =
  '/plugin/notification-ui/notification-ui.js'
export const NOTIFICATIONS_API_BASE = '/notifications/api'
export const NOTIFICATIONS_LOG_PREFIX = '[Helios][Notifications] '
export const REGISTRATION_TIMEOUT_MS = 10_000
export const NOTIFICATION_LINK_EVENT = 'notification-link'

export interface CommentLink {
  accountId: string
  threadId: string
  commentId: string
}

const AUTH_COMPLETED_EVENT = 'cwanAuthCompleted'

type SidenavWindow = Window & {
  sidenav?: { getAccessTokenSilentlyOrLogout?: unknown }
}

const reportedDocuments = new WeakSet<Document>()

function report(document: Document, cause: string): void {
  if (reportedDocuments.has(document)) return
  reportedDocuments.add(document)
  console.error(NOTIFICATIONS_LOG_PREFIX + cause)
}

function findOrCreateScript(document: Document): HTMLElement {
  const existing = document.getElementById(NOTIFICATION_UI_SCRIPT_ID)
  if (existing) return existing
  const script = document.createElement('script')
  script.id = NOTIFICATION_UI_SCRIPT_ID
  ;(document.head ?? document.body).appendChild(script)
  return script
}

export function loadNotificationUiScript(document: Document): void {
  const script = findOrCreateScript(document)
  if (script.hasAttribute('src')) return

  script.addEventListener(
    'error',
    () => report(document, 'bundle failed to load'),
    { once: true },
  )
  script.addEventListener(
    'load',
    () => {
      if (customElements.get(NOTIFICATIONS_TAG)) return
      setTimeout(() => {
        if (!customElements.get(NOTIFICATIONS_TAG)) {
          report(
            document,
            `bundle loaded but did not register <${NOTIFICATIONS_TAG}>`,
          )
        }
      }, REGISTRATION_TIMEOUT_MS)
    },
    { once: true },
  )
  script.setAttribute('src', NOTIFICATION_UI_SCRIPT_PATH)
}

function hasTokenSource(window: Window): boolean {
  return (
    typeof (window as SidenavWindow).sidenav?.getAccessTokenSilentlyOrLogout ===
    'function'
  )
}

export function whenSidenavReady(window: Window): Promise<void> {
  if (hasTokenSource(window)) return Promise.resolve()
  return new Promise<void>(resolve => {
    const onAuthCompleted = () => {
      if (!hasTokenSource(window)) return
      window.removeEventListener(AUTH_COMPLETED_EVENT, onAuthCompleted)
      resolve()
    }
    window.addEventListener(AUTH_COMPLETED_EVENT, onAuthCompleted)
  })
}

const COMMENT_LINK_PARAMS = ['commentAccountId', 'commentThreadId', 'commentId']

// Opening in place keeps the current route and URL, so only a link that is
// nothing but the app root plus the three comment ids is taken; any other
// path, param or hash would be lost and must open where it points.
export function readCommentLink(
  event: Event,
  origin: string,
  baseHref: string,
): CommentLink | undefined {
  if (!(event instanceof CustomEvent)) return undefined
  const href: unknown = event.detail?.href
  if (typeof href !== 'string') return undefined

  let url: URL
  try {
    url = new URL(href)
  } catch {
    return undefined
  }
  if (url.origin !== origin) return undefined
  const basePath = new URL(baseHref, origin).pathname.replace(/\/$/, '')
  if (url.pathname !== basePath && url.pathname !== `${basePath}/`) {
    return undefined
  }
  if (url.href.includes('#')) return undefined

  let paramCount = 0
  url.searchParams.forEach(() => paramCount++)
  if (paramCount !== COMMENT_LINK_PARAMS.length) return undefined

  const [accountId, threadId, commentId] = COMMENT_LINK_PARAMS.map(key =>
    url.searchParams.get(key),
  )
  if (!accountId || !threadId || !commentId) return undefined
  return { accountId, threadId, commentId }
}
